import { FormControl, FormGroup, Validators } from '@angular/forms';
import { DesignatedUnitFormGroup } from '../../../../../../employers/add-employer/forms/designated-unit-form-group';
import { DesignatedUnitGeneralDetailsFormGroup } from '../../../../../../employers/add-employer/forms/designated-unit-general-details-form-group';
import { DesignatedUnitContributionDetailsFormGroup } from '../../../../../../employers/add-employer/forms/designated-unit-contribution-details-form-group';
import { uniqueDesignatedUnitNameFromListValidator } from '../../../../../../../validators/unique-designated-unit-name.validator';
import { uniqueClientUnitIdFromListValidator } from '../../../../../../../validators/unique-client-unit-id-validator/unique-client-unit-id.validator';
import { contributionRatePercentageValidator } from '../../../../../../../validators/contribution-rate-percentage.validator';
import { contributionRateCalculationTypeValidator } from '../../../../../../../validators/contribution-rate-calculation-type.validator';
import { minimumPercentageRangeValidator } from '../../../../../../../validators/minimum-percentage-range.validator';
import { isoDateStringValidator } from '../../../../../../../validators/iso-date-string-validator';
import { dateRangeValidator } from '../../../../../../../validators/date-range-validator';
import { endDateRequiredWhenBackdatedValidator } from '../../../../../../../validators/end-date-required-when-backdated.validator';
import { DesignatedUnitParticipationTypeIdEnum } from '../../../../../../../models/employers/enum/designated-unit-participation-type-id.enum';

export class CreateDesignatedUnitFormFactory {
  public static createDesignatedUnitFormGroup(
    existingUnitNames: string[],
    existingClientUnitIds: string[]
  ): DesignatedUnitFormGroup {
    return new FormGroup({
      generalDetails: this.createGeneralDetailsFormGroup(existingUnitNames, existingClientUnitIds),
      contributionDetails: this.createContributionDetailsFormGroup()
    });
  }

  private static createGeneralDetailsFormGroup(
    existingUnitNames: string[],
    existingClientUnitIds: string[]
  ): DesignatedUnitGeneralDetailsFormGroup {
    return new FormGroup(
      {
        unitName: new FormControl<string | null>(null, {
          validators: [Validators.required, uniqueDesignatedUnitNameFromListValidator(existingUnitNames)]
        }),
        clientUnitId: new FormControl<string | null>(null, {
          validators: [Validators.required, uniqueClientUnitIdFromListValidator(existingClientUnitIds)]
        }),
        localId: new FormControl<number | null>(null),
        startDate: new FormControl<string | null>(null, {
          validators: [Validators.required, isoDateStringValidator]
        }),
        endDate: new FormControl<string | null>(null, { validators: [isoDateStringValidator] }),
        designatedUnitTypeId: new FormControl<number | null>(null, { validators: [Validators.required] }),
        remittanceFrequencyTypeId: new FormControl<number | null>(null, { validators: [Validators.required] })
      },
      {
        validators: [
          dateRangeValidator('startDate', 'endDate'),
          endDateRequiredWhenBackdatedValidator('startDate', 'endDate')
        ]
      }
    );
  }

  private static createContributionDetailsFormGroup(): DesignatedUnitContributionDetailsFormGroup {
    return new FormGroup({
      contributionRateBasisTypeId: new FormControl<number | null>(null, { validators: [Validators.required] }),
      matchedContributionRateTypeId: new FormControl<number | null>(null, {
        validators: [
          Validators.required,
          contributionRateCalculationTypeValidator('matchedContributionRateTypeId', 'unmatchedContributionRateTypeId')
        ]
      }),
      matchedContributionRatePercentage: new FormControl<number | null>(null, {
        validators: [Validators.required, Validators.max(9), Validators.min(1)]
      }),
      matchedContributionRateRangeFirstPercentage: new FormControl<number | null>(null, {
        validators: [
          Validators.required,
          contributionRatePercentageValidator(
            'matchedContributionRateRangeFirstPercentage',
            'unmatchedContributionRateRangeFirstPercentage'
          ),
          minimumPercentageRangeValidator(
            'matchedContributionRateRangeFirstPercentage',
            'matchedContributionRateRangeSecondPercentage'
          ),
          Validators.max(9),
          Validators.min(1)
        ]
      }),
      matchedContributionRateRangeSecondPercentage: new FormControl<number | null>(null, {
        validators: [Validators.required, Validators.max(9), Validators.min(1)]
      }),
      unmatchedContributionRateTypeId: new FormControl<number | null>(null, {
        validators: [
          Validators.required,
          contributionRateCalculationTypeValidator('matchedContributionRateTypeId', 'unmatchedContributionRateTypeId')
        ]
      }),
      unmatchedContributionRatePercentage: new FormControl<number | null>(null, {
        validators: [Validators.required, Validators.max(9), Validators.min(1)]
      }),
      unmatchedContributionRateRangeFirstPercentage: new FormControl<number | null>(null, {
        validators: [
          Validators.required,
          contributionRatePercentageValidator(
            'matchedContributionRateRangeFirstPercentage',
            'unmatchedContributionRateRangeFirstPercentage'
          ),
          minimumPercentageRangeValidator(
            'unmatchedContributionRateRangeFirstPercentage',
            'unmatchedContributionRateRangeSecondPercentage'
          ),
          Validators.max(9),
          Validators.min(1)
        ]
      }),
      unmatchedContributionRateRangeSecondPercentage: new FormControl<number | null>(null, {
        validators: [Validators.required, Validators.max(9), Validators.min(1)]
      }),
      fullTimeParticipationType: new FormControl<string>(
        { value: $localize`Mandatory`, disabled: true },
        { nonNullable: true }
      ),
      fullTimeWaitingPeriod: new FormControl<number | null>(null, {
        validators: [Validators.required, Validators.max(24)]
      }),
      lessThanFullTimeParticipationTypeId: new FormControl<number>(DesignatedUnitParticipationTypeIdEnum.Optional, {
        validators: [Validators.required],
        nonNullable: true
      }),
      lessThanFullTimeWaitingPeriod: new FormControl<number | null>(null, {
        validators: [Validators.required, Validators.max(24)]
      })
    });
  }
}
